// src/App.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link as RouterLink,
} from 'react-router-dom';
import {
  Box,
  CssBaseline,
  AppBar,
  Toolbar,
  Button,
  Drawer,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Container,
  Typography,
} from '@mui/material';
import api from './services/api';
import { logoutUser } from './services/firebaseAuth';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthContext } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/Layout/Navbar';
import MobileNav from './components/Layout/MobileNav';
import Footer from './components/Layout/Footer';
import VerificationBanner from './components/Layout/VerificationBanner';
import PromoAccessBanner from './components/Layout/PromoAccessBanner';
import VerificationExplainerModal from './components/Layout/VerificationExplainerModal';
import MFAModal from './components/Auth/MFAModal';
import MFAWaitingScreen from './components/Auth/MFAWaitingScreen';
import VerificationSuccessScreen from './components/Auth/VerificationSuccessScreen';
import RetryVerificationModal from './components/Auth/RetryVerificationModal';
import Home from './components/Home/Home';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import ForgotPassword from './components/Auth/ForgotPassword';
import Welcome from './components/Auth/Welcome';
import Dashboard from './components/Dashboard/Dashboard';
import Creators from './components/Creators/Creators';
import Leaderboard from './components/Leaderboard/Leaderboard';
import Reviews from './components/Reviews/Reviews';
import CreatorPage from './components/Creators/CreatorPage';
import SubscriptionPage from './components/Stripe/SubscriptionPage';
import PaymentPage from './components/Stripe/PaymentPage';
import SuccessPage from './components/Stripe/SuccessPage';
import TermsOfService from './components/Legal/TermsOfService';
import PrivacyPolicy from './components/Legal/PrivacyPolicy';
import AdminReportsDashboard from './components/Admin/AdminReportsDashboard';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';
import Support from './components/Support/Support';
import API from './components/config';

export default function App() {
  const { authData, setAuthData } = useContext(AuthContext);
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isSm = useMediaQuery(theme.breakpoints.down('md'));
  
  // Capture referral code and visitor_id from URL on app load
  // Rewardful uses ?via=TOKEN format, but we also support ?ref=CODE for backwards compatibility
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    // Check for Rewardful's format first (?via=TOKEN)
    const viaCode = urlParams.get('via');
    // Fallback to ?ref=CODE format
    const refCode = urlParams.get('ref') || viaCode;
    
    if (refCode) {
      // Store referral code in localStorage so it persists across page navigation
      localStorage.setItem('rewardful_ref', refCode);
    }
    
    // Capture Rewardful visitor_id if available (from cookie or API)
    // This is important for linking conversions to referrals
    try {
      if (window.rewardful) {
        // Wait for Rewardful to be ready
        window.rewardful('ready', function() {
          try {
            // Try to get visitor_id from Rewardful API
            let visitorId = null;
            if (window.rewardful.visitor && window.rewardful.visitor.id) {
              visitorId = window.rewardful.visitor.id;
            } else {
              // Fallback: try to get from cookie
              const cookies = document.cookie.split(';');
              for (let cookie of cookies) {
                const [name, value] = cookie.trim().split('=');
                if (name === '_rwid' || name === 'rewardful_visitor_id') {
                  visitorId = value;
                  break;
                }
              }
            }
            
            if (visitorId) {
              localStorage.setItem('rewardful_visitor_id', visitorId);
            }
          } catch (e) {
            // Silently handle visitor_id capture errors
          }
        });
      } else {
        // If Rewardful not loaded yet, try to get from cookie directly
        const cookies = document.cookie.split(';');
        for (let cookie of cookies) {
          const [name, value] = cookie.trim().split('=');
          if (name === '_rwid' || name === 'rewardful_visitor_id') {
            localStorage.setItem('rewardful_visitor_id', value);
            break;
          }
        }
      }
    } catch (e) {
      // Silently handle visitor_id capture errors
    }
  }, []);
  
  // Global verification state
  const [userProfile, setUserProfile] = useState(null);
  const [showMFAModal, setShowMFAModal] = useState(false);
  const [showMFAWaitingScreen, setShowMFAWaitingScreen] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [showExplainerModal, setShowExplainerModal] = useState(false);
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [triggeringMFA, setTriggeringMFA] = useState(false);
  const [pendingSuccessScreen, setPendingSuccessScreen] = useState(false);
  const [isRefreshingSubscriptions, setIsRefreshingSubscriptions] = useState(false);
  const [forceShowBanner, setForceShowBanner] = useState(false);
  
  // Fetch user profile for verification status
  useEffect(() => {
    if (authData?.access_token) {
      fetchUserProfile();
      
      // Poll for verification status every 3 seconds if not verified
      const interval = setInterval(() => {
        if (userProfile && !userProfile.verified) {
          fetchUserProfile();
        }
      }, 3000);
      
      return () => clearInterval(interval);
    } else {
      setUserProfile(null);
    }
  }, [authData?.access_token]);
  
  // Check for MFA code sent and show modal
  useEffect(() => {
    if (userProfile?.mfaCodeSent && !showMFAModal) {
      setShowMFAModal(true);
    }
  }, [userProfile?.mfaCodeSent]);

  const fetchUserProfile = useCallback(async () => {
    if (!authData) return;
    
    try {
      const { data } = await api.get(`${API}/api/dashboard`);
      setUserProfile(data.profile);
      
      // Update authData with tutorial status from database
      if (authData && data.profile.hasOwnProperty('hasSeenTutorial') && setAuthData) {
        setAuthData(prev => ({
          ...prev,
          isFirstLogin: !data.profile.hasSeenTutorial
        }));
      }
      
      return data.profile; // Return for use in callbacks
    } catch (err) {
      // Check if account is blocked
      if (err.response?.status === 403 && err.response?.data?.error?.toLowerCase().includes('blocked')) {
        const errorMessage = err.response.data.error;
        console.error('Account blocked:', errorMessage);
        toast.error(errorMessage, { autoClose: 10000 });
        // Log out the user
        await logoutUser();
        return null;
      }
      // Silent fail - user might not be on a page that requires auth yet
      console.error('Failed to fetch user profile:', err);
      return null;
    }
  }, [authData]);

  // Background polling for verification status changes
  useEffect(() => {
    if (!authData || !userProfile) return;

    // Only poll if user is not verified yet
    if (userProfile.verified) return;

    // Only poll if verification is in progress
    const activeStatuses = ['processing', 'queued', 'mfa_triggered', 'mfa_code_submitted'];
    if (!activeStatuses.includes(userProfile.verificationStatus)) return;

    const startTime = Date.now();
    const TIMEOUT_MS = 360000; // 6 minutes (1 minute buffer after backend timeout)

    const pollInterval = setInterval(async () => {
      try {
        // Check if we've exceeded timeout
        if (Date.now() - startTime > TIMEOUT_MS) {
          clearInterval(pollInterval);
          toast.error('Verification timed out. Please try again.', { autoClose: 8000 });
          await fetchUserProfile(); // Refresh to get latest status
          return;
        }

        const { data } = await api.get(
          `${API}/api/subscriptions/verification-status`
        );

        // If backend returned failed status, stop polling
        if (data.verificationStatus === 'failed') {
          clearInterval(pollInterval);
          await fetchUserProfile();
          return;
        }

        // Update profile if verification status changed
        if (data.verificationStatus !== userProfile.verificationStatus) {
          fetchUserProfile(); // Refresh to get latest data
        }

        // If MFA code was sent, show modal
        if (data.mfaCodeSent && !showMFAModal) {
          setShowMFAModal(true);
          fetchUserProfile();
        }

        // If verified, refresh and check if we should show success screen
          if (data.verified) {
            setIsRefreshingSubscriptions(false); // Reset refresh flag
            setForceShowBanner(false); // Hide forced banner
            clearInterval(pollInterval);
          await fetchUserProfile();
          // Check if tutorial/onboarding is currently active
          const tutorialKey = authData?.user_id ? `tutorial_completed_${authData.user_id}` : 'tutorialCompleted';
          const isTutorialActive = authData?.isFirstLogin && !localStorage.getItem(tutorialKey);
          if (isTutorialActive) {
            setPendingSuccessScreen(true);
          } else {
            // Use setTimeout to ensure state is updated before showing success screen
            setTimeout(() => {
              setShowSuccessScreen(true);
            }, 500);
          }
          return; // Exit polling
        }
      } catch (err) {
        // Don't clear interval on error - keep trying
      }
    }, 5000); // Poll every 5 seconds

    // Cleanup on unmount or when dependencies change
    return () => {
      clearInterval(pollInterval);
    };
  }, [authData, userProfile?.verified, userProfile?.verificationStatus, showMFAModal, fetchUserProfile]);
  
  // Check if tutorial completed and we have a pending success screen
  useEffect(() => {
    if (!pendingSuccessScreen || !userProfile?.verified) return;
    
    // Poll for tutorial completion every second
    const checkInterval = setInterval(() => {
      const tutorialKey = authData?.user_id ? `tutorial_completed_${authData.user_id}` : 'tutorialCompleted';
      const tutorialCompleted = localStorage.getItem(tutorialKey);
      
      if (tutorialCompleted) {
        clearInterval(checkInterval);
        setPendingSuccessScreen(false);
        setShowSuccessScreen(true);
      }
    }, 1000);
    
    return () => clearInterval(checkInterval);
  }, [pendingSuccessScreen, userProfile?.verified]);
  
  const handleStartMFA = async () => {
    setTriggeringMFA(true);
    
    // Force banner to show immediately
    console.log('🚀 [APP] Forcing banner to show immediately');
    setForceShowBanner(true);
    
    // Check if user is already verified - if so, this is a refresh
    const isRefresh = userProfile?.verified;
    setIsRefreshingSubscriptions(isRefresh);
    
    // Immediately set status to 'processing' so banner shows right away
    if (userProfile) {
      setUserProfile({
        ...userProfile,
        verificationStatus: 'processing',
        verified: isRefresh ? true : false // Keep verified status if refreshing
      });
    }
    
    try {
      await api.post(
        `${API}/api/subscriptions/trigger-mfa-verification`,
        {}
      );
      
      // Show full-screen waiting experience
      setShowMFAWaitingScreen(true);
      
      // Poll for mfaCodeSent flag
      const startTime = Date.now();
      const pollInterval = setInterval(async () => {
        try {
          // Check for timeout
          if (Date.now() - startTime > 360000) { // 6 minutes
            clearInterval(pollInterval);
            setShowMFAWaitingScreen(false);
            toast.error('Verification timed out. Please try again.', { autoClose: 8000 });
            await fetchUserProfile();
            return;
          }

          const { data } = await api.get(
            `${API}/api/subscriptions/verification-status`
          );
          
          // Check for failed status
          if (data.verificationStatus === 'failed') {
            clearInterval(pollInterval);
            setShowMFAWaitingScreen(false);
            await fetchUserProfile();
            toast.error('Verification failed. Please try again.');
            return;
          }
          
          if (data.mfaCodeSent) {
            // Hide waiting screen, show MFA modal
            setShowMFAWaitingScreen(false);
            setShowMFAModal(true);
            await fetchUserProfile();
            clearInterval(pollInterval);
            return;
          }
          
          if (data.verified) {
            setShowMFAWaitingScreen(false);
            setIsRefreshingSubscriptions(false); // Reset refresh flag
            setForceShowBanner(false); // Hide forced banner
            clearInterval(pollInterval);
            await fetchUserProfile();
            // Check if tutorial is active
            const tutorialKey = authData?.user_id ? `tutorial_completed_${authData.user_id}` : 'tutorialCompleted';
            const isTutorialActive = authData?.isFirstLogin && !localStorage.getItem(tutorialKey);
            if (isTutorialActive) {
              setPendingSuccessScreen(true);
            } else {
              setTimeout(() => {
                setShowSuccessScreen(true);
              }, 500);
            }
            return;
          }
        } catch (err) {
          clearInterval(pollInterval);
          setShowMFAWaitingScreen(false);
          toast.error('Error checking verification status');
        }
      }, 2000);
      
    } catch (err) {
      toast.error('Failed to start verification');
      setShowMFAWaitingScreen(false);
    } finally {
      setTriggeringMFA(false);
    }
  };
  
  const handleMFASuccess = () => {
    toast.success('MFA code submitted! Completing verification...');
    
    // Poll for completion
    const startTime = Date.now();
    const pollInterval = setInterval(async () => {
      try {
        // Check for timeout
        if (Date.now() - startTime > 360000) { // 6 minutes
          clearInterval(pollInterval);
          toast.error('Verification timed out. Please try again.', { autoClose: 8000 });
          await fetchUserProfile();
          return;
        }

        const { data } = await api.get(
          `${API}/api/subscriptions/verification-status`
        );
        
        // Check for failed status
        if (data.verificationStatus === 'failed') {
          clearInterval(pollInterval);
          await fetchUserProfile();
          toast.error('Verification failed. Please check your code and try again.');
          return;
        }
        
        if (data.verified) {
          setIsRefreshingSubscriptions(false); // Reset refresh flag
          setForceShowBanner(false); // Hide forced banner
          clearInterval(pollInterval);
          await fetchUserProfile();
          // Check if tutorial is active
          const tutorialKey = authData?.user_id ? `tutorial_completed_${authData.user_id}` : 'tutorialCompleted';
          const isTutorialActive = authData?.isFirstLogin && !localStorage.getItem(tutorialKey);
          if (isTutorialActive) {
            setPendingSuccessScreen(true);
          } else {
            setTimeout(() => {
              setShowSuccessScreen(true);
            }, 500);
          }
          return;
        }
      } catch (err) {
        clearInterval(pollInterval);
        toast.error('Error checking verification status');
      }
    }, 2000); // Check every 2 seconds for faster feedback
  };
  
  const handleRetryVerification = () => {
    setShowRetryModal(true);
  };
  
  const handleRetrySuccess = () => {
    toast.success('Verification requeued! We\'ll process your account shortly.');
    fetchUserProfile();
  };

  return (
    <Router>
      <CssBaseline />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          overflowX: 'hidden',
          bgcolor: theme.palette.background.default,
        }}
      >
        {/* Use Navbar component for all cases */}
        <Navbar />
        
        {/* Global Verification Banner - Shows on all pages when logged in */}
        {authData && userProfile && (
          <VerificationBanner
            key={`${userProfile.verificationStatus}-${userProfile.mfaCodeSent}`}
            user={userProfile}
            isRefreshing={isRefreshingSubscriptions}
            forceShow={forceShowBanner}
            onStartMFA={handleStartMFA}
            onLearnMore={() => setShowExplainerModal(true)}
            onRetryVerification={handleRetryVerification}
          />
        )}

        {authData && userProfile && <PromoAccessBanner user={userProfile} />}

        {/* Main content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            pt: { xs: 7, sm: 8, md: 9 }, // toolbar offset
            pb: authData && isSm ? 9 : 2, // space for mobile nav
            width: '100%',
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Container 
            maxWidth="lg" 
            sx={{ 
              width: '100%',
              px: { xs: 2, sm: 3, md: 4 },
            }}
          >
            <Routes>
              <Route
                path="/"
                element={authData ? <Dashboard /> : <Home />}
              />
              <Route path="/home" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/welcome" element={<Welcome />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/creators" element={<ProtectedRoute><Creators /></ProtectedRoute>} />
              <Route path="/reviews" element={<ProtectedRoute><Reviews /></ProtectedRoute>} />
              <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
              <Route path="/creator/:id" element={<CreatorPage />} />
              <Route
                path="/subscription"
                element={<SubscriptionPage />}
              />
              <Route
                path="/payment"
                element={<PaymentPage />}
              />
              <Route
                path="/success"
                element={<SuccessPage />}
              />
              <Route
                path="/terms"
                element={<TermsOfService />}
              />
              <Route
                path="/privacy"
                element={<PrivacyPolicy />}
              />
              <Route
                path="/admin/reports"
                element={<ProtectedAdminRoute><AdminReportsDashboard /></ProtectedAdminRoute>}
              />
              <Route
                path="/support"
                element={<ProtectedRoute><Support /></ProtectedRoute>}
              />
            </Routes>
          </Container>
        </Box>

        {/* Mobile bottom nav */}
        {authData && isSm && (
          <MobileNav />
        )}

        {/* Footer */}
        <Box sx={{ mt: 'auto' }}>
          <Footer />
        </Box>

        <ToastContainer 
          position="top-center" 
          autoClose={3000} 
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        
        {/* MFA Waiting Screen - Full screen experience */}
        {showMFAWaitingScreen && (
          <MFAWaitingScreen onClose={() => setShowMFAWaitingScreen(false)} />
        )}

        {/* Success Screen - Shows after verification complete */}
        {showSuccessScreen && userProfile && (
          <VerificationSuccessScreen
            subscriptions={userProfile.subscriptions || []}
            onClose={() => {
              setShowSuccessScreen(false);
              toast.success('✅ You can now review creators!');
            }}
          />
        )}

        {/* Global MFA Modal - Can appear on any page */}
        <MFAModal
          open={showMFAModal}
          onClose={() => setShowMFAModal(false)}
          onSuccess={() => {
            setShowMFAModal(false);
            handleMFASuccess();
          }}
          token={authData?.access_token}
        />
        
        {/* Verification Explainer Modal */}
        <VerificationExplainerModal
          open={showExplainerModal}
          onClose={() => setShowExplainerModal(false)}
        />
        
        {/* Retry Verification Modal */}
        <RetryVerificationModal
          open={showRetryModal}
          onClose={() => setShowRetryModal(false)}
          onSuccess={() => {
            setShowRetryModal(false);
            handleRetrySuccess();
          }}
          token={authData?.access_token}
          errorMessage={userProfile?.verificationError}
        />
      </Box>
    </Router>
  );
}
